import dummyFunction from '@/lib/utils/dummyFunction';
import useComplexState from '@/lib/utils/useComplexState';
import { createContext } from 'react';
import type React from 'react';
import { v4 as generateUuid } from 'uuid';

type PendingChangesSaveFunction = () => Promise<void>;

const PortfolioItemCloseBlockingContext = createContext<{
  isPortfolioItemCloseBlocked: boolean;
  blockPortfolioItemClose: (
    savePendingChanges: PendingChangesSaveFunction,
  ) => string;
  unblockPortfolioItemClose: (blockerId: string) => void;
  saveAllPendingPortfolioItemChanges: () => Promise<void>;
}>({
  isPortfolioItemCloseBlocked: false,
  blockPortfolioItemClose: () => '',
  unblockPortfolioItemClose: dummyFunction,
  saveAllPendingPortfolioItemChanges: () => Promise.resolve(),
});

type PortfolioItemCloseBlockingContextProviderProps = {
  children: React.ReactNode;
};

export const PortfolioItemCloseBlockingContextProvider = ({
  children,
}: PortfolioItemCloseBlockingContextProviderProps) => {
  const [portfolioItemCloseBlockers, setPortfolioItemCloseBlockers] =
    useComplexState(new Map<string, PendingChangesSaveFunction>());

  const blockPortfolioItemClose = (
    savePendingChanges: PendingChangesSaveFunction,
  ): string => {
    const blockerId = generateUuid();

    setPortfolioItemCloseBlockers(portfolioItemCloseBlockers => {
      portfolioItemCloseBlockers.set(blockerId, savePendingChanges);
    });

    return blockerId;
  };

  const unblockPortfolioItemClose = (blockerId: string): void => {
    setPortfolioItemCloseBlockers(portfolioItemCloseBlockers => {
      portfolioItemCloseBlockers.delete(blockerId);
    });
  };

  const saveAllPendingPortfolioItemChanges = async (): Promise<void> => {
    await Promise.all(
      Array.from(portfolioItemCloseBlockers.values()).map(savePendingChanges =>
        savePendingChanges(),
      ),
    );
  };

  return (
    <PortfolioItemCloseBlockingContext.Provider
      value={{
        isPortfolioItemCloseBlocked: portfolioItemCloseBlockers.size > 0,
        blockPortfolioItemClose,
        unblockPortfolioItemClose,
        saveAllPendingPortfolioItemChanges,
      }}
    >
      {children}
    </PortfolioItemCloseBlockingContext.Provider>
  );
};

export default PortfolioItemCloseBlockingContext;
